import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { isNil, range } from 'lodash';

import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Router } from '@angular/router';
import { MeterDataStoreServiceDirective } from '@app/shared/services/store/meter-data-store.service';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { PageEvent } from '../../models/page-event';

@Component({
  selector: 'app-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss'],
})
export class PaginatorComponent implements OnChanges {
  private _length: number | undefined = 0;
  public _resultsPerPage = 0;
  public _selectedResults: any;

  @Input() visiblePages = 5;
  @Input() pageSize?: number = 0;
  @Input() previousHoverMessage?: string = '';
  @Input() nextHoverMessage?: string = '';
  @Input()
  set resultsPerPage(value: any) {
    if (!isNil(value)) {
      this._resultsPerPage = value;
      this._selectedResults = this.pageSize && this.pageSize > 0 ? this.pageSize?.toString() : value[value.length - 1];
    }
  }

  @Input()
  set length(value: number) {
    if (value !== 0 && value !== this._length) {
      this.updatedVisiblePages = this.visiblePages;
      this.updatePagination(value);
    }
    this._length = value;
  }

  @Input() rowCount?: number;

  updatedVisiblePages = this.visiblePages;

  @Output() page = new EventEmitter<PageEvent>();

  @Input() index = 1;
  @Input() showConfirmation = false;
  @Input() confirmationMessage = '';
  @Input() confirmationYes = '';
  @Input() confirmationNo = '';
  indexes: number[] = [];
  pageIndexes: number[] = [];

  constructor(private router: Router, public meterDataStoreServiceDirective: MeterDataStoreServiceDirective, public matDialog: MatDialog) {}

  public get lastIndex() {
    return this.indexes[this.indexes.length - 1];
  }

  public get firstIndex() {
    return this.indexes[0];
  }

  updatePagination(length: number) {
    const upperRange = this.pageSize && this.pageSize > 0 ? Math.ceil(length / this.pageSize) : 1;
    this.indexes = range(1, upperRange + 1);
    this.updatedVisiblePages = this.visiblePages >= this.lastIndex ? this.lastIndex : this.visiblePages;
    this.pageIndexes = range(this.index, this.updatedVisiblePages + this.index);
    this.updateIndexes();
    this.updateSelect(this.index);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      this.router.url.includes('energiedaten') &&
      !this.router.url.includes('energiedatenv2') &&
      changes.length.previousValue !== changes.length.currentValue
    ) {
      this.updatedVisiblePages = this.visiblePages;
      this.updatePagination(changes.length.currentValue);
    }
    if (changes.rowCount) {
      this._length = this.rowCount;
      this.updatePagination(this._length as number);
    }
    if (changes['index']) {
      this.updateIndexes();
    }
  }

  next() {
    this.showConfirmationDialog(() => this.updateNext());
  }

  private updateNext() {
    if (this.index === this.lastIndex) {
      return;
    }

    this.index += 1;
    this.updateIndexes();
    this.emitPageEvent();
  }

  back() {
    this.showConfirmationDialog(() => this.updateBack());
  }

  private updateBack() {
    if (this.index === this.firstIndex) {
      return;
    }

    this.index -= 1;
    this.updateIndexes();
    this.emitPageEvent();
  }

  select(index: number) {
    this.showConfirmationDialog(() => this.updateSelect(index));
  }

  private updateSelect(index: number) {
    if (index === this.index) {
      return;
    }

    this.index = index;
    this.updateIndexes();
    this.emitPageEvent();
  }

  changePagination(changeEvent: MatSelectChange): void {
    this.showConfirmationDialog(
      () => this.updateChangePagination(changeEvent),
      () => this.cancelChangePagination(changeEvent)
    );
  }

  private updateChangePagination(changeEvent: MatSelectChange) {
    if (isNaN(changeEvent.value)) {
      this.pageSize = 0;
    } else {
      this.pageSize = changeEvent.value;
    }
    this.index = 1;
    this.updatePagination(this._length as number);
    this.updateIndexes();
    this.emitPageEvent();
  }

  private cancelChangePagination(changeEvent: MatSelectChange) {
    changeEvent.source.writeValue(this._selectedResults);
  }

  updateIndexes() {
    const half = this.updatedVisiblePages / 2;

    if (this.lastIndex === this.updatedVisiblePages) {
      this.pageIndexes = range(this.firstIndex, this.lastIndex + 1);
      return;
    }

    const isInStartExtreme = this.index - Math.floor(half) <= this.firstIndex;
    const isInEndExtreme = this.index + Math.floor(half) >= this.lastIndex;

    const start = isInStartExtreme ? this.firstIndex : this.index - Math.floor(half);
    const end = isInEndExtreme ? this.lastIndex + 1 : this.index + Math.ceil(half);

    const startOffset = isInEndExtreme ? Math.ceil(half) - (end - this.index) : 0;
    const endOffset = isInStartExtreme ? Math.ceil(half) - this.index : 0;

    this.pageIndexes = range(start - startOffset, end + endOffset);
  }

  emitPageEvent() {
    this.page.emit({
      index: this.index,
      length: this._length,
      pageSize: this.pageSize,
      visiblePages: this.updatedVisiblePages,
    });
  }

  trackByFn(_: number, item: number) {
    return item;
  }

  private showConfirmationDialog(callback: () => void, neinCallback?: () => void): void {
    if (this.showConfirmation) {
      this.matDialog
        .open(NotificationDialogComponent, {
          data: {
            descriptionText: this.confirmationMessage,
            confirmText: this.confirmationYes,
            declineText: this.confirmationNo,
            descriptionFaClass: 'fa-info-circle fa-2x',
          },
          panelClass: ['-md'],
        })
        .afterClosed()
        .subscribe((res) => {
          if (res) {
            callback();
          } else if (neinCallback) {
            neinCallback();
          }
        });
    } else {
      callback();
    }
  }
}
